import { useReduxState } from 'hooks';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  XAxis,
  YAxis,
  YAxisProps,
  Tooltip
} from 'recharts';

const DefaultCursor = ({
  x,
  y,
  width,
  height
}: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}) => (
  <Rectangle
    fill="#ccc"
    x={x + width / 2 - 1}
    y={y}
    width={1}
    height={height}
  />
);

interface Props<T extends { [key: string]: string | number }> {
  accessibilityLayer?: boolean;
  data: T[];
  height?: number;
  stacked?: boolean;
  tooltipFormatter?: (
    value,
    name,
    props
  ) => string | string[] | React.ReactNode;
  xAxisDataKey: string;
  yAxisDataKey: string | { label: string; color: string }[];
  yAxisDataKeysVisible?: string[];
  yAxisDomain?: YAxisProps['domain'];
  yUnit?: string;
}

const BarChart = <T extends { [key: string]: string | number }>({
  accessibilityLayer = false,
  data,
  height = 300,
  stacked = false,
  tooltipFormatter,
  xAxisDataKey,
  yAxisDataKey,
  yAxisDataKeysVisible,
  yAxisDomain,
  yUnit
}: Props<T>) => {
  const { theme } = useReduxState();

  const useRadius = (yaxis: (typeof yAxisDataKey)[number], propsData) => {
    // if yaxis is a string, this will not be called since this is
    // only used if yAxisDataKey is an Array
    if (typeof yaxis === 'string' || !Array.isArray(yAxisDataKey)) return true;

    // loop through yAxisDataKey in reverse order to find the topmost bar
    // it will be the first one that has a non-zero value in propsData
    for (let i = yAxisDataKey.length - 1; i >= 0; i--) {
      const item = yAxisDataKey[i].label;
      if (
        propsData[item] &&
        (!yAxisDataKeysVisible || yAxisDataKeysVisible.includes(item))
      )
        return yaxis.label === item;
    }
    return false;
  };

  return (
    <div className="line-chart" style={{ height, width: '100%' }}>
      <ResponsiveContainer>
        <RechartsBarChart
          accessibilityLayer={accessibilityLayer}
          data={data}
          margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
        >
          <CartesianGrid
            stroke={theme.lightGreyDarkActive}
            strokeDasharray="5 7"
            vertical={false}
          />
          <XAxis
            axisLine={false}
            dataKey={xAxisDataKey}
            dy={10}
            fontSize={14}
            fontWeight={600}
            stroke={theme.textColorLighter}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            axisLine={false}
            domain={yAxisDomain || [0, 'auto']}
            dx={-10}
            fontSize={14}
            fontWeight={600}
            stroke={theme.textColorLighter}
            tickLine={false}
            unit={yUnit || ''}
            width="auto"
          />
          {Array.isArray(yAxisDataKey) ? (
            yAxisDataKey.map((yaxis) => (
              <Bar
                key={yaxis.label}
                background={false}
                barSize={6}
                dataKey={yaxis.label}
                fill={yaxis.color}
                hide={
                  yAxisDataKeysVisible &&
                  !yAxisDataKeysVisible.includes(yaxis.label)
                }
                stroke={yaxis.color}
                {...(stacked
                  ? {
                      stackId: 'stacked',
                      shape: (props) => {
                        // we want to show a radius only for the top section of the bar
                        if (useRadius(yaxis, props)) {
                          props.radius = [4, 4, 0, 0];
                        }
                        return <Rectangle {...props} />;
                      }
                    }
                  : { radius: [20, 20, 0, 0] })}
              />
            ))
          ) : (
            <Bar
              background={false}
              barSize={6}
              dataKey={yAxisDataKey}
              fill={theme.chartMainColor}
              stroke={theme.chartMainColor}
              radius={[20, 20, 0, 0]}
            />
          )}
          <Tooltip
            animationDuration={200}
            contentStyle={{
              border: 0,
              borderRadius: 10,
              boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.2)'
            }}
            cursor={<DefaultCursor />}
            formatter={tooltipFormatter}
            itemStyle={{ color: theme.textColor }}
            labelStyle={{ color: theme.textColorLighter }}
            wrapperStyle={{
              border: 0,
              borderRadius: 3,
              fontSize: '1.4rem',
              fontWeight: 600
            }}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
